import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type {
  OrganisationRole,
  QueryFilter,
  QueryResult,
  QuerySort,
  UserDto,
  UserListItemDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryUsers = async (
  orgId?: string | null,
  input?: { page: number; pageSize: number; sorts: QuerySort[]; filters: QueryFilter[] },
): Promise<QueryResult<UserListItemDto>> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<QueryResult<UserListItemDto>>(
      'users/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 100,
        sorts: input?.sorts ?? [{ field: 'email', direction: 'asc' }],
        filters: input?.filters ?? [],
      },
      { retries: 1, orgId: orgId ?? undefined, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getUser = async (id: string): Promise<UserDto> => {
  const authToken = ensureToken();
  try {
    return await apiClient.get<UserDto>(`users/${id}`, { token: authToken, retries: 1 });
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateUser = async (input: {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}): Promise<UserDto> => {
  const authToken = ensureToken();
  const body = {
    email: input.email?.trim(),
    firstName: input.firstName?.trim(),
    lastName: input.lastName?.trim(),
  };
  try {
    return await apiClient.patch<UserDto>(`users/${input.id}`, body, {
      token: authToken,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateUserRole = async (input: {
  id: string;
  role: OrganisationRole;
}): Promise<{ status: string; role: OrganisationRole }> => {
  const authToken = ensureToken();
  try {
    return await apiClient.patch<{ status: string; role: OrganisationRole }>(
      `users/${input.id}/membership`,
      { role: input.role },
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const removeUserFromOrganisation = async (input: {
  id: string;
}): Promise<{ id: string }> => {
  const authToken = ensureToken();
  try {
    await apiClient.post<{ status: string }>(`users/${input.id}/membership/remove`, undefined, {
      token: authToken,
      retries: 1,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteUser = async (input: {
  id: string;
  password: string;
}): Promise<{ id: string }> => {
  const authToken = ensureToken();
  try {
    await apiClient.post<{ status: string }>(
      `users/${input.id}/delete`,
      { password: input.password },
      { token: authToken, retries: 1 },
    );
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const changePassword = async (input: {
  id: string;
  currentPassword: string;
  newPassword: string;
}): Promise<{ status: string }> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<{ status: string }>(
      `users/${input.id}/password`,
      { currentPassword: input.currentPassword, newPassword: input.newPassword },
      { token: authToken, retries: 1 },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateUserContext = async (input: {
  lastOrganisationId?: string | null;
  lastProjectId?: string | null;
  onboardingCompletedAt?: string | null;
}): Promise<{
  lastOrganisationId: string | null;
  lastProjectId: string | null;
  onboardingCompletedAt: string | null;
}> => {
  const authToken = ensureToken();
  const body: {
    lastOrganisationId?: string | null;
    lastProjectId?: string | null;
    onboardingCompletedAt?: string | null;
  } = {};
  if (input.lastOrganisationId !== undefined) {
    body.lastOrganisationId = input.lastOrganisationId;
  }
  if (input.lastProjectId !== undefined) {
    body.lastProjectId = input.lastProjectId;
  }
  if (input.onboardingCompletedAt !== undefined) {
    body.onboardingCompletedAt = input.onboardingCompletedAt;
  }
  try {
    return await apiClient.patch<{
      lastOrganisationId: string | null;
      lastProjectId: string | null;
      onboardingCompletedAt: string | null;
    }>('users/context', body, { token: authToken, retries: 1, ignoreOrgRemoval: true });
  } catch (err) {
    throw toApiError(err);
  }
};

export const resendEmailVerification = async (): Promise<{ status: string }> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<{ status: string }>('users/verify-email/resend', undefined, {
      token: authToken,
      retries: 1,
    });
  } catch (err) {
    throw toApiError(err);
  }
};
